import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertTriangle, ShieldAlert, Shield, ShieldCheck, Loader2, RefreshCw,
  ChevronLeft, ChevronRight, Clock, Globe, Mail, User, Zap, CheckCircle2,
  XCircle, Eye, Download, Trash2
} from "lucide-react";

const severityConfig = {
  CRITICAL: { color: "bg-red-600 text-white", border: "border-red-300 bg-red-50", icon: ShieldAlert, label: "Critical" },
  HIGH: { color: "bg-orange-500 text-white", border: "border-orange-300 bg-orange-50", icon: AlertTriangle, label: "High" },
  MEDIUM: { color: "bg-amber-500 text-white", border: "border-amber-300 bg-amber-50", icon: Shield, label: "Medium" },
};

const patternConfig = {
  FAILED_LOGIN_IP: { icon: Globe, label: "Failed Login (IP)", color: "text-red-600" },
  FAILED_LOGIN_EMAIL: { icon: Mail, label: "Failed Login (Account)", color: "text-orange-600" },
  RAPID_BOOKINGS: { icon: Zap, label: "Rapid Bookings", color: "text-amber-600" },
  FAILED_REGISTRATIONS_IP: { icon: User, label: "Failed Registrations", color: "text-purple-600" },
  BULK_ADMIN_ACTIONS: { icon: ShieldAlert, label: "Bulk Admin Actions", color: "text-red-700" },
};

const statusConfig = {
  active: { color: "bg-red-100 text-red-700 border-red-200", label: "Active" },
  acknowledged: { color: "bg-blue-100 text-blue-700 border-blue-200", label: "Acknowledged" },
  resolved: { color: "bg-green-100 text-green-700 border-green-200", label: "Resolved" },
  dismissed: { color: "bg-slate-100 text-slate-600 border-slate-200", label: "Dismissed" },
};

function AlertCard({ alert, onAction, selected, onSelect }) {
  const sev = severityConfig[alert.severity] || severityConfig.MEDIUM;
  const pat = patternConfig[alert.pattern_type] || { icon: AlertTriangle, label: alert.pattern_type, color: "text-slate-600" };
  const PatIcon = pat.icon;
  const st = statusConfig[alert.status] || statusConfig.active;

  const formatTime = (ts) => {
    if (!ts) return "-";
    const d = new Date(ts);
    return d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", hour12: false });
  };

  const renderEvidence = (ev) => {
    if (!ev) return null;
    const items = [];
    if (ev.ip) items.push({ label: "IP Address", value: ev.ip });
    if (ev.email) items.push({ label: "Email", value: ev.email });
    if (ev.user_id) items.push({ label: "User ID", value: ev.user_id });
    if (ev.attempt_count) items.push({ label: "Attempts", value: ev.attempt_count });
    if (ev.booking_count) items.push({ label: "Bookings", value: ev.booking_count });
    if (ev.action_count) items.push({ label: "Actions", value: ev.action_count });
    if (ev.window_minutes) items.push({ label: "Window", value: `${ev.window_minutes} min` });
    return items;
  };

  const evidenceItems = renderEvidence(alert.evidence);

  return (
    <Card data-testid={`alert-card-${alert.alert_id}`} className={`border-2 ${sev.border} transition-all hover:shadow-md`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <Checkbox checked={selected} onCheckedChange={(checked) => onSelect(alert.alert_id, Boolean(checked))} data-testid={`select-alert-${alert.alert_id}`} />
            <div className={`p-2 rounded-lg ${sev.color}`}>
              <PatIcon className="w-5 h-5" />
            </div>
            <div>
              <CardTitle className="text-base font-heading uppercase tracking-tight">{alert.title}</CardTitle>
              <p className="text-xs text-muted-foreground mt-0.5">{alert.description}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Badge className={`${sev.color} text-[10px] font-heading uppercase`}>{sev.label}</Badge>
            <Badge variant="outline" className={`${st.color} text-[10px] font-heading uppercase`}>{st.label}</Badge>
            {alert.trigger_count > 1 && (
              <Badge variant="outline" className="text-[10px] font-heading">x{alert.trigger_count}</Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {evidenceItems?.map((item, i) => (
              <div key={i} className="text-sm">
                <p className="text-[10px] font-heading uppercase tracking-wider text-muted-foreground">{item.label}</p>
                <p className="font-mono text-xs mt-0.5 break-all">{item.value}</p>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <div className="flex items-center gap-1 text-xs text-muted-foreground mr-3">
              <Clock className="w-3 h-3" />
              {formatTime(alert.created_at)}
            </div>
            {alert.status === "active" && (
              <>
                <Button data-testid={`ack-btn-${alert.alert_id}`} size="sm" variant="outline"
                  className="h-7 text-xs rounded-none border-2 font-heading uppercase"
                  onClick={() => onAction(alert.alert_id, "acknowledged")}>
                  <Eye className="w-3 h-3 mr-1" /> Ack
                </Button>
                <Button data-testid={`resolve-btn-${alert.alert_id}`} size="sm"
                  className="h-7 text-xs rounded-none bg-green-600 hover:bg-green-700 text-white font-heading uppercase"
                  onClick={() => onAction(alert.alert_id, "resolved")}>
                  <CheckCircle2 className="w-3 h-3 mr-1" /> Resolve
                </Button>
                <Button data-testid={`dismiss-btn-${alert.alert_id}`} size="sm" variant="ghost"
                  className="h-7 text-xs text-muted-foreground font-heading uppercase"
                  onClick={() => onAction(alert.alert_id, "dismissed")}>
                  <XCircle className="w-3 h-3 mr-1" /> Dismiss
                </Button>
              </>
            )}
            {alert.status === "acknowledged" && (
              <>
                <Button data-testid={`resolve-btn-${alert.alert_id}`} size="sm"
                  className="h-7 text-xs rounded-none bg-green-600 hover:bg-green-700 text-white font-heading uppercase"
                  onClick={() => onAction(alert.alert_id, "resolved")}>
                  <CheckCircle2 className="w-3 h-3 mr-1" /> Resolve
                </Button>
                <Button data-testid={`dismiss-btn-${alert.alert_id}`} size="sm" variant="ghost"
                  className="h-7 text-xs text-muted-foreground font-heading uppercase"
                  onClick={() => onAction(alert.alert_id, "dismissed")}>
                  <XCircle className="w-3 h-3 mr-1" /> Dismiss
                </Button>
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function SecurityAlerts() {
  const [alerts, setAlerts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("active");
  const [severityFilter, setSeverityFilter] = useState("all");
  const [patternFilter, setPatternFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [selectedAlertIds, setSelectedAlertIds] = useState([]);
  const limit = 20;

  const fetchAlerts = async () => {
    setLoading(true);
    try {
      const params = { limit, skip: page * limit };
      if (statusFilter !== "all") params.status = statusFilter;
      if (severityFilter !== "all") params.severity = severityFilter;
      if (patternFilter !== "all") params.pattern = patternFilter;
      const res = await api.get("/admin/security-alerts", { params });
      setAlerts(res.data.alerts || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error("Failed to fetch alerts:", err);
      setAlerts([]);
    }
    setLoading(false);
  };

  const fetchStats = async () => {
    try {
      const res = await api.get("/admin/security-alerts/stats");
      setStats(res.data);
    } catch (err) {
      console.error("Failed to fetch stats:", err);
    }
  };

  useEffect(() => {
    fetchAlerts();
    fetchStats();
  }, [page, statusFilter, severityFilter, patternFilter]);

  useEffect(() => {
    setSelectedAlertIds([]);
  }, [page, statusFilter, severityFilter, patternFilter]);

  const handleAction = async (alertId, newStatus) => {
    try {
      await api.put(`/admin/security-alerts/${alertId}/resolve`, { status: newStatus });
      fetchAlerts();
      fetchStats();
    } catch (err) {
      console.error("Failed to update alert:", err);
    }
  };

  const handleRefresh = () => {
    fetchAlerts();
    fetchStats();
  };

  const allVisibleSelected = alerts.length > 0 && alerts.every((alert) => selectedAlertIds.includes(alert.alert_id));
  const exportLabel = selectedAlertIds.length > 0 ? `Selected (${selectedAlertIds.length})` : `Filtered (${total})`;

  const toggleAlertSelection = (alertId, checked) => {
    setSelectedAlertIds((prev) => checked ? [...new Set([...prev, alertId])] : prev.filter((id) => id !== alertId));
  };

  const toggleSelectAllVisible = (checked) => {
    setSelectedAlertIds((prev) => checked ? [...new Set([...prev, ...alerts.map((alert) => alert.alert_id)])] : prev.filter((id) => !alerts.some((alert) => alert.alert_id === id)));
  };

  const exportCsv = async () => {
    try {
      const params = { format: "csv", status: statusFilter, severity: severityFilter, pattern: patternFilter };
      if (selectedAlertIds.length > 0) params.alert_ids = selectedAlertIds.join(",");
      const res = await api.get("/admin/security-alerts/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "security-alerts.csv";
      a.click();
    } catch {
      toast.error("Failed to export alerts CSV");
    }
  };

  const exportPdf = () => {
    const targetAlerts = selectedAlertIds.length > 0 ? alerts.filter((alert) => selectedAlertIds.includes(alert.alert_id)) : alerts;
    const win = window.open("", "_blank");
    if (!win) return;
    const rows = targetAlerts.map((alert) => `<tr><td>${alert.alert_id}</td><td>${alert.title}</td><td>${alert.severity}</td><td>${alert.status}</td><td>${alert.pattern_type}</td><td>${alert.created_at || ""}</td></tr>`).join("");
    win.document.write(`
      <html><head><title>Security Alerts Summary</title><style>
        body { font-family: Arial, sans-serif; padding: 24px; }
        table { width: 100%; border-collapse: collapse; margin-top: 20px; }
        th, td { border: 1px solid #ddd; padding: 8px; font-size: 12px; text-align: left; }
        th { background: #f5f5f5; text-transform: uppercase; letter-spacing: 0.08em; }
      </style></head><body>
        <h1>Security Alerts Summary</h1>
        <p>Export scope: ${exportLabel}</p>
        <table><thead><tr><th>Alert ID</th><th>Title</th><th>Severity</th><th>Status</th><th>Pattern</th><th>Created</th></tr></thead><tbody>${rows}</tbody></table>
      </body></html>
    `);
    win.document.close();
    win.focus();
    win.print();
  };

  const bulkDelete = async () => {
    const confirmed = window.confirm(`Delete ${exportLabel.toLowerCase()} alerts? This cannot be undone.`);
    if (!confirmed) return;
    try {
      const res = await api.post("/admin/security-alerts/bulk-delete", {
        scope: selectedAlertIds.length > 0 ? "selected" : "filtered",
        alert_ids: selectedAlertIds,
        status: statusFilter,
        severity: severityFilter,
        pattern: patternFilter,
      });
      toast.success(`Deleted ${res.data.deleted_count} alerts`);
      setSelectedAlertIds([]);
      fetchAlerts();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete alerts");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 md:py-10" data-testid="security-alerts-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight flex items-center gap-3">
            <ShieldAlert className="w-8 h-8 text-red-500" />
            Security Alerts
          </h1>
          <p className="text-muted-foreground mt-1">Real-time suspicious activity monitoring</p>
        </div>
        <Button data-testid="refresh-alerts-btn" onClick={handleRefresh} variant="outline" className="rounded-none border-2 font-heading uppercase">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      <div className="flex flex-col lg:flex-row gap-3 mb-4">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Checkbox checked={allVisibleSelected} onCheckedChange={(checked) => toggleSelectAllVisible(Boolean(checked))} data-testid="select-all-visible-alerts" />
          <span>{selectedAlertIds.length} selected</span>
        </div>
        <div className="flex flex-wrap gap-2 lg:ml-auto">
          <Button data-testid="export-alerts-csv-btn" variant="outline" className="rounded-none border-2 font-heading uppercase" onClick={exportCsv}>
            <Download className="w-4 h-4 mr-2" />CSV ({exportLabel})
          </Button>
          <Button data-testid="export-alerts-pdf-btn" variant="outline" className="rounded-none border-2 font-heading uppercase" onClick={exportPdf}>
            <Download className="w-4 h-4 mr-2" />PDF ({exportLabel})
          </Button>
          <Button data-testid="delete-alerts-btn" variant="outline" className="rounded-none border-2 font-heading uppercase text-red-600 border-red-200" onClick={bulkDelete}>
            <Trash2 className="w-4 h-4 mr-2" />Delete ({exportLabel})
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <Card className="border-2 border-red-200 bg-red-50/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-heading uppercase tracking-wider text-red-600">Active</CardTitle>
            </CardHeader>
            <CardContent>
              <p data-testid="stat-active" className="text-2xl font-bold text-red-700">{stats.active}</p>
            </CardContent>
          </Card>
          <Card className="border-2 border-blue-200 bg-blue-50/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-heading uppercase tracking-wider text-blue-600">Acknowledged</CardTitle>
            </CardHeader>
            <CardContent>
              <p data-testid="stat-acknowledged" className="text-2xl font-bold text-blue-700">{stats.acknowledged}</p>
            </CardContent>
          </Card>
          <Card className="border-2 border-green-200 bg-green-50/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-heading uppercase tracking-wider text-green-600">Resolved Today</CardTitle>
            </CardHeader>
            <CardContent>
              <p data-testid="stat-resolved" className="text-2xl font-bold text-green-700">{stats.resolved_today}</p>
            </CardContent>
          </Card>
          <Card className="border-2">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-heading uppercase tracking-wider text-muted-foreground">Dismissed</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{stats.dismissed}</p>
            </CardContent>
          </Card>
          <Card className="border-2">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-heading uppercase tracking-wider text-muted-foreground">Total All Time</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{stats.total}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Severity Breakdown */}
      {stats?.by_severity && (
        <div className="flex flex-wrap gap-2 mb-6">
          {Object.entries(stats.by_severity).map(([sev, count]) => {
            const conf = severityConfig[sev] || severityConfig.MEDIUM;
            return (
              <button key={sev} onClick={() => setSeverityFilter(severityFilter === sev ? "all" : sev)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-heading uppercase tracking-wide transition-all ${
                  severityFilter === sev ? "bg-court-orange text-white" : `${conf.border} hover:opacity-80`
                }`} data-testid={`severity-badge-${sev}`}>
                <conf.icon className="w-3.5 h-3.5" />
                {sev}: {count}
              </button>
            );
          })}
          {Object.entries(stats.by_pattern || {}).filter(([, c]) => c > 0).map(([pat, count]) => {
            const conf = patternConfig[pat] || { icon: AlertTriangle, label: pat };
            return (
              <button key={pat} onClick={() => setPatternFilter(patternFilter === pat ? "all" : pat)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-heading uppercase tracking-wide transition-all ${
                  patternFilter === pat ? "bg-court-orange text-white" : "bg-slate-100 text-slate-700 hover:opacity-80"
                }`} data-testid={`pattern-badge-${pat}`}>
                <conf.icon className="w-3.5 h-3.5" />
                {conf.label}: {count}
              </button>
            );
          })}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-3 mb-6">
        <Select value={statusFilter} onValueChange={v => { setStatusFilter(v); setPage(0); }}>
          <SelectTrigger data-testid="alert-status-filter" className="w-full md:w-44 h-11 rounded-none border-2">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="acknowledged">Acknowledged</SelectItem>
            <SelectItem value="resolved">Resolved</SelectItem>
            <SelectItem value="dismissed">Dismissed</SelectItem>
          </SelectContent>
        </Select>
        <Select value={severityFilter} onValueChange={v => { setSeverityFilter(v); setPage(0); }}>
          <SelectTrigger data-testid="alert-severity-filter" className="w-full md:w-44 h-11 rounded-none border-2">
            <SelectValue placeholder="Severity" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Severity</SelectItem>
            <SelectItem value="CRITICAL">Critical</SelectItem>
            <SelectItem value="HIGH">High</SelectItem>
            <SelectItem value="MEDIUM">Medium</SelectItem>
          </SelectContent>
        </Select>
        <Select value={patternFilter} onValueChange={v => { setPatternFilter(v); setPage(0); }}>
          <SelectTrigger data-testid="alert-pattern-filter" className="w-full md:w-52 h-11 rounded-none border-2">
            <SelectValue placeholder="Pattern" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Patterns</SelectItem>
            <SelectItem value="FAILED_LOGIN_IP">Failed Login (IP)</SelectItem>
            <SelectItem value="FAILED_LOGIN_EMAIL">Failed Login (Account)</SelectItem>
            <SelectItem value="RAPID_BOOKINGS">Rapid Bookings</SelectItem>
            <SelectItem value="FAILED_REGISTRATIONS_IP">Failed Registrations</SelectItem>
            <SelectItem value="BULK_ADMIN_ACTIONS">Bulk Admin Actions</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Results Count + Pagination */}
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-muted-foreground">{total} alert{total !== 1 ? "s" : ""} found</p>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0}
            className="h-8 w-8 p-0 rounded-none border-2">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-sm">Page {page + 1}</span>
          <Button variant="outline" size="sm" onClick={() => setPage(page + 1)} disabled={(page + 1) * limit >= total}
            className="h-8 w-8 p-0 rounded-none border-2">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Alert Cards */}
      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-red-500" />
        </div>
      ) : alerts.length === 0 ? (
        <Card className="border-2 border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <ShieldCheck className="w-12 h-12 text-green-500 mb-3" />
            <p className="font-heading text-lg uppercase tracking-tight text-green-700">All Clear</p>
            <p className="text-sm text-muted-foreground mt-1">No suspicious activity detected</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="alerts-list">
          {alerts.map(alert => (
            <AlertCard key={alert.alert_id} alert={alert} onAction={handleAction} selected={selectedAlertIds.includes(alert.alert_id)} onSelect={toggleAlertSelection} />
          ))}
        </div>
      )}
    </div>
  );
}
